// « Journée » — the day's register (PMS-TERMINAL-LINK-BRIEF §8.8p, operator
// design 2026-09-27). Read-only, no form.
//
// The pages are the shifts: Départs the check-out shift, Arrivées the
// check-in one, each with its « Autres encaissements » (guests on neither
// sheet: the tab a form payment was taken on, else check-in). The register is
// the two together, by tender, checked against Vega's own total for the day.
// Below it, what each shift left behind:
//
//   Départs   what was collected; « Parti sans régler » — a guest who left
//             with a note still pending (a missed payment); « Mis en compte »
//             — notes put on a debtor account, not missed.
//   Arrivées  what was collected; « Non réglé à l'arrivée » — flagged always
//             (operator 2026-09-27); the no-shows — Vega cancels a no-show,
//             which takes it off the live book, so they are read from its
//             cancellations: reason, value lost, and the money on the booking
//             (a deposit kept, a fee) or « rien dans Vega ». The desk's
//             clerical cancellations (Err, Double resa, Block, Dans le groupe)
//             are counted, not listed. Past its day, a booking still « not
//             arrived » in Vega is a no-show Vega was never told about.
//
// Opens on today (operator). At go-live the adapters change, not this screen.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ArrivalBill,
  ArrivalBriefing,
  DayTakingsLine,
  DepartureBill,
  DepartureBriefing,
  IsoDate,
  NoShow,
  OtherTaking,
  PaymentLine,
  TakingsBriefing,
} from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate } from './logic.js';
import {
  arrivalOnDay,
  clientTag,
  dayMonth,
  drawerCents,
  othersTakings,
  pageTakings,
  registerBlocks,
  splitByDay,
  splitBySettlement,
  takingsSummary,
  toCollectCents,
  todayParis,
  transferredCents,
  vegaReadLabel,
  type TakingsLine,
} from './departureSheet.js';
import { DayBar } from './DayBar.js';
import { OtherTakings } from './OtherTakings.js';
import { PaymentList } from './PaymentList.js';
import { RoomList } from './RoomList.js';
import { SheetTop } from './SheetTop.js';
import { SHEET_REFRESH_MS, useAutoRefresh } from './useAutoRefresh.js';
import { EtablissementTag } from './EtablissementTag.js';
import { usePmsReachOrEmpty } from './PmsProvider.js';

export interface JourneeProps {
  arrivals: ArrivalBriefing;
  departures: DepartureBriefing;
  takings: TakingsBriefing;
  /** The view chips, placed at the top by the Encaissement screen. */
  nav?: ReactNode;
  /** How often the register re-reads itself while on screen; 0 turns it off. */
  refreshMs?: number;
  /** Injected for tests; the wall clock otherwise. */
  now?: Date;
}

const plural = (n: number) => (n > 1 ? 's' : '');

/** « 16:37 » — Vega's cancellation time, 'HHMM'. */
const hm = (t: string | null) => (t && /^\d{4}$/.test(t) ? `${t.slice(0, 2)}:${t.slice(2)}` : '');

function Register({
  checkout,
  checkin,
  vega,
  othersOut,
  othersIn,
}: {
  checkout: TakingsLine[];
  checkin: TakingsLine[];
  vega: DayTakingsLine[] | null;
  othersOut: number;
  othersIn: number;
}) {
  const out = takingsSummary(checkout);
  const inn = takingsSummary(checkin);
  const day = takingsSummary([...checkout, ...checkin]);
  const v = vega ? takingsSummary(vega) : null;
  // Espèces always — it is the drawer — then every tender that moved.
  const cols = day.tiles.filter((t) => t.bucket === 'cash' || t.movements > 0 || (v?.tiles.find((x) => x.bucket === t.bucket)?.movements ?? 0) > 0);
  const cell = (s: typeof day, bucket: string) => {
    const t = s.tiles.find((x) => x.bucket === bucket)!;
    return t.movements === 0 ? '—' : fmtAmount(t.netCents);
  };
  const gap = v ? day.totalCents - v.totalCents : 0;
  const outside = [...out.outside, ...inn.outside];
  return (
    <>
      <div className="pms-table-wrap">
        <table className="pms-table pms-register" aria-label="Caisse de la journée">
          <thead>
            <tr>
              <th />
              {cols.map((c) => (
                <th key={c.bucket} className="pms-num">
                  {c.label}
                </th>
              ))}
              <th className="pms-num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Check-out <span className="pms-sub">(départs{othersOut ? `, dont ${othersOut} autre${plural(othersOut)}` : ''})</span>
              </td>
              {cols.map((c) => (
                <td key={c.bucket} className="pms-num">
                  {cell(out, c.bucket)}
                </td>
              ))}
              <td className="pms-num">{fmtAmount(out.totalCents)}</td>
            </tr>
            <tr>
              <td>
                Check-in <span className="pms-sub">(arrivées{othersIn ? `, dont ${othersIn} autre${plural(othersIn)}` : ''})</span>
              </td>
              {cols.map((c) => (
                <td key={c.bucket} className="pms-num">
                  {cell(inn, c.bucket)}
                </td>
              ))}
              <td className="pms-num">{fmtAmount(inn.totalCents)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>Journée</strong>
              </td>
              {cols.map((c) => (
                <td key={c.bucket} className="pms-num">
                  <strong>{cell(day, c.bucket)}</strong>
                </td>
              ))}
              <td className="pms-num">
                <strong>{fmtAmount(day.totalCents)}</strong>
              </td>
            </tr>
            {v && (
              <tr>
                <td className="pms-sub">Total Vega du jour</td>
                {cols.map((c) => (
                  <td key={c.bucket} className="pms-num pms-sub">
                    {cell(v, c.bucket)}
                  </td>
                ))}
                <td className="pms-num">
                  {gap === 0 ? (
                    <span className="pms-ok" title="La journée est égale au total que Vega enregistre pour ce jour">
                      {fmtAmount(v.totalCents)} ✓
                    </span>
                  ) : (
                    <span
                      className="pms-stale"
                      title="Écart entre la journée et le total Vega du jour : un encaissement non rattaché, ou pas encore relu"
                    >
                      {fmtAmount(v.totalCents)} · écart {fmtAmount(gap)}
                    </span>
                  )}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      {outside.length > 0 && (
        <p className="pms-hint">
          Hors caisse, non compté : {outside.map((o) => `${o.label} ${fmtAmount(o.cents)}`).join(' · ')}
        </p>
      )}
      {day.notInVega > 0 && (
        <p className="pms-hint pms-stale">
          {day.notInVega} encaissement{plural(day.notInVega)} pris au formulaire, pas encore saisi{plural(day.notInVega)} dans
          Vega
        </p>
      )}
    </>
  );
}

function Section({ title, count, children }: { title: string; count: number; children?: ReactNode }) {
  return (
    <>
      <div className="pms-section-bar">
        <h4 className="pms-register-sub">
          {title} <span className="pms-sub">({count})</span>
        </h4>
      </div>
      {children}
    </>
  );
}

/** Who paid on the day, with their lines of the day. */
function Collected({
  rows,
  label,
  day,
}: {
  rows: { key: string; rooms: string[]; name: ReactNode; lines: PaymentLine[] }[];
  label: string;
  day: IsoDate;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="pms-table-wrap">
      <table className="pms-table pms-register-list" aria-label={label}>
        <tbody>
          {rows.map((r) => {
            const total = r.lines.reduce((t, p) => t + p.cents, 0);
            return (
              <tr key={r.key}>
                <td>
                  <RoomList rooms={r.rooms} />
                </td>
                <td>{r.name}</td>
                <td className="pms-num">
                  {fmtAmount(total)}
                  <PaymentList payments={r.lines} day={day} total={total} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NoShows({ rows, notArrived, day, past }: { rows: NoShow[]; notArrived: ArrivalBill[]; day: IsoDate; past: boolean }) {
  const listed = rows.filter((n) => !n.clerical);
  const clerical = rows.filter((n) => n.clerical);
  return (
    <>
      <Section title="No-shows" count={listed.length + (past ? notArrived.length : 0)} />
      <div className="pms-table-wrap">
        <table className="pms-table pms-register-list" aria-label="No-shows">
          <tbody>
            {listed.map((n) => (
              <tr key={n.bookingRef}>
                <td>
                  résa {n.bookingRef}
                  {n.name && <span className="pms-sub"> · {n.name}</span>}
                </td>
                <td>
                  {n.reason ?? '—'}
                  <span className="pms-sub">
                    {' '}
                    · annulée le {dayMonth(n.cancelledOn)} {hm(n.cancelledAt)}
                    {n.lostCents !== null && ` · séjour ${fmtAmount(n.lostCents)}`}
                  </span>
                </td>
                <td>
                  {n.payments.length === 0 ? (
                    <span className="pms-sub">rien dans Vega</span>
                  ) : (
                    <ul className="pms-pay-list">
                      {n.payments.map((p, i) => (
                        <li key={i} className="pms-pay">
                          <span className="pms-pay-when">{dayMonth(p.date)}</span>{' '}
                          <span className="pms-pay-how">{p.label ?? p.method}</span>{' '}
                          <span className="pms-pay-amt">{fmtAmount(p.cents)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            ))}
            {past &&
              notArrived.map((a) => (
                <tr key={a.bookingRef}>
                  <td>
                    <RoomList rooms={a.rooms} /> · {a.partyLabel}
                    <span className="pms-sub"> {clientTag(a.clientRef, a.bookingRef)}</span>
                    <EtablissementTag code={a.propertyCode} />
                  </td>
                  <td colSpan={2} className="pms-stale">
                    toujours attendu dans Vega le {dayMonth(day)} — no-show non traité
                  </td>
                </tr>
              ))}
            {listed.length === 0 && !(past && notArrived.length > 0) && (
              <tr>
                <td colSpan={3} className="pms-empty">
                  Aucun no-show.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {clerical.length > 0 && (
        <p className="pms-hint">
          {clerical.length} annulation{plural(clerical.length)} administrative{plural(clerical.length)} écartée
          {plural(clerical.length)} : {clerical.map((n) => `${n.bookingRef} (${n.reason ?? '—'})`).join(', ')}
        </p>
      )}
    </>
  );
}

export function Journee({ arrivals, departures, takings, nav, refreshMs = SHEET_REFRESH_MS, now }: JourneeProps) {
  const [date, setDate] = useState<IsoDate>(() => todayParis(now ?? new Date()));
  const [arr, setArr] = useState<ArrivalBill[]>([]);
  const [dep, setDep] = useState<DepartureBill[]>([]);
  const [others, setOthers] = useState<OtherTaking[]>([]);
  const [noShows, setNoShows] = useState<NoShow[]>([]);
  const [vega, setVega] = useState<DayTakingsLine[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++latest.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      const [a, d, o, n, v] = await Promise.all([
        arrivals.arrivals(date),
        departures.departures(date),
        takings.dayOthers(date),
        takings.dayNoShows(date),
        takings.dayTakings(date),
      ]);
      if (mine !== latest.current) return;
      setLoading(false);
      const failed = [a, d, o, n, v].find((x) => !x.ok);
      setError(failed && !failed.ok ? (failed.error.message ?? 'lecture impossible') : null);
      if (a.ok) setArr(a.value);
      if (d.ok) setDep(d.value);
      if (o.ok) setOthers(o.value);
      if (n.ok) setNoShows(n.value);
      setVega(v.ok ? v.value : null);
    },
    [arrivals, departures, takings, date],
  );

  useEffect(() => {
    void load();
  }, [load]);
  const quietLoad = useCallback(() => void load(true), [load]);
  useAutoRefresh(quietLoad, refreshMs);

  const clock = now ?? new Date();
  const today = todayParis(clock);
  const past = date < today;
  const dm = dayMonth(date);

  const depDay = useMemo(() => dep.map((b) => ({ b, on: splitByDay(b.payments, date).onDay })), [dep, date]);
  const arrDay = useMemo(() => arr.map((a) => ({ a, m: arrivalOnDay(a, date) })), [arr, date]);
  const checkout = useMemo(
    () => [...pageTakings(depDay.flatMap((x) => x.on)), ...othersTakings(others, 'checkout')],
    [depDay, others],
  );
  const checkin = useMemo(
    () => [...pageTakings(arrDay.flatMap((x) => x.m.onDay)), ...othersTakings(others, 'checkin')],
    [arrDay, others],
  );
  const split = useMemo(() => splitBySettlement(dep), [dep]);
  const missed = split.toCollect.filter((b) => b.checkedOut && toCollectCents(b) > 0);
  const stillIn = split.toCollect.filter((b) => !b.checkedOut && toCollectCents(b) > 0);
  const unpaidArrivals = arrDay.filter((x) => x.a.arrived && x.m.dueCents > 0);
  const notArrived = arr.filter((a) => !a.arrived);
  const othersOut = others.filter((o) => o.shift === 'checkout');
  const othersIn = others.filter((o) => o.shift === 'checkin');
  const read = vegaReadLabel(arr.find((a) => a.readAt)?.readAt ?? dep.find((b) => b.readAt)?.readAt ?? null, clock);

  // U3 (M11′): with several établissements in reach, one register EACH — a
  // figure is one établissement's — and the one line that adds them, the
  // single cash drawer (UD4). With one, the register below is unchanged.
  const reach = usePmsReachOrEmpty();
  const blocks = useMemo(
    () =>
      reach.length > 1
        ? registerBlocks(
            reach.map((b) => ({ code: b.property.code, labelFr: b.property.labelFr })),
            depDay,
            arrDay,
            others,
            vega,
          )
        : [],
    [reach, depDay, arrDay, others, vega],
  );

  return (
    <section className="pms-print" aria-label="Journée">
      <SheetTop nav={nav} onRefresh={() => void load()} canPrint />
      <DayBar value={date} onChange={setDate} today={today} label="Journée du" />
      <div className="pms-head">
        <div>
          <h2>Journée du {fmtDate(date)}</h2>
          <p className="pms-hint">
            {read && (
              <span className={read.stale ? 'pms-stale' : undefined}>
                {read.stale ? '⚠ ' : ''}
                {read.text}
                {' · '}
              </span>
            )}
            Lecture seule · check-out = les départs du jour, check-in = les arrivées du jour, chacun avec ses autres
            encaissements (clients ni arrivés ni partis : l’onglet où ils ont été pris au formulaire, sinon les
            arrivées).
          </p>
        </div>
      </div>
      {error && <p className="pms-warn">⚠ {error}</p>}
      {loading && <p className="pms-hint pms-no-print">Chargement…</p>}

      <h3 className="pms-section-title">Caisse de la journée</h3>
      {blocks.length > 0 ? (
        <>
          {blocks.map((b) => (
            <div key={b.property.code} data-testid="register-block">
              <h4 className="pms-register-etab">{b.property.labelFr}</h4>
              <Register
                checkout={b.checkout}
                checkin={b.checkin}
                vega={b.vega}
                othersOut={b.othersOut}
                othersIn={b.othersIn}
              />
            </div>
          ))}
          <p className="pms-drawer" data-testid="drawer-line">
            Tiroir de la réception — espèces, chèques et ANCV des {blocks.length} établissements (un seul
            tiroir) : <strong>{fmtAmount(drawerCents(blocks))}</strong>
            <span className="pms-sub"> · à rapprocher du tiroir, pas un chiffre d’encaissement</span>
          </p>
        </>
      ) : (
        <Register
          checkout={checkout}
          checkin={checkin}
          vega={vega}
          othersOut={othersOut.filter((o) => o.bucket !== 'outside').length}
          othersIn={othersIn.filter((o) => o.bucket !== 'outside').length}
        />
      )}

      <h3 className="pms-section-title">Départs du {dm} — check-out</h3>
      <Section title="Encaissé" count={depDay.filter((x) => x.on.length > 0).length} />
      <Collected
        day={date}
        label="Départs encaissés"
        rows={depDay
          .filter((x) => x.on.length > 0)
          .map((x) => ({
            key: `${x.b.bookingRef}-${x.b.unit ?? ''}`,
            rooms: x.b.rooms,
            name: (
              <>
                {x.b.partyLabel}
                <span className="pms-sub"> {clientTag(x.b.clientRef, x.b.bookingRef)}</span>
                <EtablissementTag code={x.b.propertyCode} />
              </>
            ),
            lines: x.on,
          }))}
      />
      <Section title="Parti sans régler" count={missed.length} />
      {missed.length > 0 && (
        <div className="pms-table-wrap">
          <table className="pms-table pms-register-list" aria-label="Parti sans régler">
            <tbody>
              {missed.map((b) => (
                <tr key={`${b.bookingRef}-${b.unit ?? ''}`}>
                  <td>
                    <RoomList rooms={b.rooms} />
                  </td>
                  <td>
                    {b.partyLabel}
                    <span className="pms-sub"> {clientTag(b.clientRef, b.bookingRef)}</span>
                    <EtablissementTag code={b.propertyCode} />
                    {b.pendingNotes.length > 0 && (
                      <div className="pms-sub">note{plural(b.pendingNotes.length)} en attente {b.pendingNotes.map((n) => n.facture).join(', ')}</div>
                    )}
                  </td>
                  <td className="pms-num">
                    <strong className="pms-missed">{fmtAmount(toCollectCents(b))}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {stillIn.length > 0 && (
        <p className="pms-hint">
          {past ? 'Pas partis dans Vega' : 'Pas encore partis'}, encore à encaisser :{' '}
          {stillIn.map((b) => `${b.partyLabel} ${fmtAmount(toCollectCents(b))}`).join(' · ')}
        </p>
      )}
      <Section title="Mis en compte" count={split.debtors.length} />
      {split.debtors.length > 0 && (
        <p className="pms-hint">
          {split.debtors
            .map((b) => `${b.partyLabel} ${fmtAmount(transferredCents(b))}${b.debtCents ? ` (reste dû ${fmtAmount(b.debtCents)})` : ''}`)
            .join(' · ')}
        </p>
      )}
      <Section title="Autres encaissements — service départs" count={othersOut.length} />
      <OtherTakings rows={othersOut} day={date} label="Autres encaissements du service départs" />

      <h3 className="pms-section-title">Arrivées du {dm} — check-in</h3>
      <Section title="Encaissé" count={arrDay.filter((x) => x.m.onDay.length > 0).length} />
      <Collected
        day={date}
        label="Arrivées encaissées"
        rows={arrDay
          .filter((x) => x.m.onDay.length > 0)
          .map((x) => ({
            key: x.a.bookingRef,
            rooms: x.a.rooms,
            name: (
              <>
                {x.a.partyLabel}
                <span className="pms-sub"> {clientTag(x.a.clientRef, x.a.bookingRef)}</span>
                <EtablissementTag code={x.a.propertyCode} />
              </>
            ),
            lines: x.m.onDay,
          }))}
      />
      <Section title="Non réglé à l’arrivée" count={unpaidArrivals.length} />
      {unpaidArrivals.length > 0 && (
        <div className="pms-table-wrap">
          <table className="pms-table pms-register-list" aria-label="Non réglé à l’arrivée">
            <tbody>
              {unpaidArrivals.map(({ a, m }) => (
                <tr key={a.bookingRef}>
                  <td>
                    <RoomList rooms={a.rooms} />
                  </td>
                  <td>
                    {a.partyLabel}
                    <span className="pms-sub"> {clientTag(a.clientRef, a.bookingRef)}</span>
                    <EtablissementTag code={a.propertyCode} />
                  </td>
                  <td className="pms-num">
                    <strong>{fmtAmount(m.dueCents)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NoShows rows={noShows} notArrived={notArrived} day={date} past={past} />
      {!past && notArrived.length > 0 && (
        <p className="pms-hint">
          {notArrived.length} pas encore arrivé{plural(notArrived.length)} — no-show seulement une fois la journée passée.
        </p>
      )}
      <Section title="Autres encaissements — service arrivées" count={othersIn.length} />
      <OtherTakings rows={othersIn} day={date} label="Autres encaissements du service arrivées" />
    </section>
  );
}
